import React, { useEffect, useMemo } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { QuantumProviderValue } from '@quantum/tools/context';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { GuardProps } from '@quantum/tools/guard';
import { QuantumReduxProvider } from '@quantum/tools/redux';
import { attemptToReload } from '../../utils/common';
import RemoteAppProps from '../../../../types/RemoteAppProps';
import { PageModel } from '../../../../assembler-server/types/PageModel';
import { AEMPageProps } from '../../../../modules/remote-ucl/types/UCL';
import { QuantumProviderWrapper } from '../../components/QuantumProviderWrapper';
import { ResetRedirectCounter } from './ResetRedirectCounter';
import { Page, PageProps } from './Page';
import { ProtectedRoute } from './ProtectedRoute';
import { Error } from './Error';

interface AppProps extends RemoteAppProps {
  children: React.ReactElement<AEMPageProps & { brand: string }>;
  guardProps?: GuardProps;
}

function RedirectToSSR(props: Omit<PageProps, 'pagePath' | 'path'>) {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!pathname.match(/^\/{2,}/g)) {
      attemptToReload(pathname);
    }
  }, [pathname]);
  return (
    <Page
      path={'*'}
      pagePath={''}
      aemHost={props.aemHost}
      model={props.model}
      brand={props.brand}
      deviceType={props.deviceType}>
      {props.children}
    </Page>
  );
}

export function App({ routes, model, aemHost, brand, language, country, children, store, deviceType, guardProps, productName }: AppProps) {
  const context = useMemo(
    () => ({
      brand: brand,
      locale: `${language}-${country}`,
      deviceType,
      tenant: productName
    }),
    [brand, language, country, deviceType, productName]
  );

  const FallBackComponent = useMemo(() => {
    // eslint-disable-next-line react/display-name
    return ({ resetErrorBoundary }: FallbackProps) => (
      <Error
        routes={routes}
        resetErrorBoundary={resetErrorBoundary}
      />
    );
  }, [routes]);

  return (
    <QuantumProviderWrapper value={context as QuantumProviderValue}>
      <QuantumReduxProvider store={store}>
        <Routes>
          {routes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <ErrorBoundary FallbackComponent={FallBackComponent}>
                  <ProtectedRoute
                    pageModel={(model[':children']?.[route.pagePath] ?? {}) as PageModel}
                    guardProps={guardProps}
                    path={route.path}>
                    <ResetRedirectCounter>
                      <Page
                        key={route.path}
                        path={route.path}
                        pagePath={route.pagePath}
                        aemHost={aemHost}
                        model={model}
                        brand={brand}
                        deviceType={deviceType}>
                        {children}
                      </Page>
                    </ResetRedirectCounter>
                  </ProtectedRoute>
                </ErrorBoundary>
              }
            />
          ))}
          <Route
            path={'*'}
            element={
              <RedirectToSSR
                aemHost={aemHost}
                model={model}
                brand={brand}
                deviceType={deviceType}>
                {children}
              </RedirectToSSR>
            }
          />
        </Routes>
      </QuantumReduxProvider>
    </QuantumProviderWrapper>
  );
}
